export type TypesenseGeopoint = [number, number];
export type AlgoliaGeolocPoint = { lat: number; lng: number };

function isFiniteCoord(value: unknown): value is number {
  return typeof value === 'number' && Number.isFinite(value);
}

function pointFromObject(value: unknown): AlgoliaGeolocPoint | null {
  if (!value || typeof value !== 'object') return null;
  const lat = (value as { lat?: unknown }).lat;
  const lng = (value as { lng?: unknown }).lng;
  if (!isFiniteCoord(lat) || !isFiniteCoord(lng)) return null;
  return { lat, lng };
}

function pointFromPair(value: unknown): AlgoliaGeolocPoint | null {
  if (!Array.isArray(value) || value.length < 2) return null;
  const lat = value[0];
  const lng = value[1];
  if (!isFiniteCoord(lat) || !isFiniteCoord(lng)) return null;
  return { lat, lng };
}

/** Map Algolia `_geoloc` (object or array) to Typesense `geopoint[]`. */
export function geolocFromAlgoliaJob(source: Record<string, unknown>): TypesenseGeopoint[] | undefined {
  const raw = source._geoloc;
  if (!raw) return undefined;

  const points: AlgoliaGeolocPoint[] = [];
  const seen = new Set<string>();

  const addPoint = (point: AlgoliaGeolocPoint | null) => {
    if (!point) return;
    const key = `${point.lat},${point.lng}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push(point);
  };

  if (Array.isArray(raw)) {
    for (const item of raw) {
      addPoint(pointFromObject(item) ?? pointFromPair(item));
    }
  } else {
    addPoint(pointFromObject(raw));
  }

  if (points.length === 0) return undefined;
  return points.map((p) => [p.lat, p.lng]);
}

/** Map Typesense `geoloc` back to Algolia-shaped `_geoloc` for MapView. */
export function algoliaGeolocFromTypesenseGeoloc(
  geoloc: unknown,
): AlgoliaGeolocPoint[] | undefined {
  if (!geoloc) return undefined;

  const points: AlgoliaGeolocPoint[] = [];
  const seen = new Set<string>();

  const addPoint = (point: AlgoliaGeolocPoint | null) => {
    if (!point) return;
    const key = `${point.lat},${point.lng}`;
    if (seen.has(key)) return;
    seen.add(key);
    points.push(point);
  };

  if (Array.isArray(geoloc)) {
    for (const item of geoloc) {
      addPoint(pointFromPair(item) ?? pointFromObject(item));
    }
  } else {
    addPoint(pointFromObject(geoloc));
  }

  return points.length > 0 ? points : undefined;
}
